import {
  AnimatePresence,
  animate,
  type DragControls,
  type MotionProps,
  motion,
  useDragControls,
  useMotionValue,
} from 'motion/react'
import {
  type ComponentProps,
  createContext,
  type Dispatch,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { mergeProps } from 'react-aria'
import {
  Dialog as AriaDialog,
  Heading as AriaHeading,
  Modal as AriaModal,
  ModalContext as AriaModalContext,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components'
import { tv } from 'tailwind-variants'
import { UseState } from 'src/components/Common/UseState'
import { UIButton, type UIButtonProps } from 'src/components/ui/Button'
import Text from 'src/components/ui/Text'
import { cn } from 'src/utils/cx'
import { dynamicCreateElement } from 'src/utils/dynamic'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { isAppWebview } from 'src/utils/userAgent'

const MotionAriaModalOverlay = motion.create(AriaModalOverlay)
const MotionAriaModal = motion.create(AriaModal)

export interface UIModalProps {
  type?: 'dialog' | 'drawer'
  open?: boolean
  onClose?: () => void
  /**
   * Whether the modal can be dismissed
   *
   * - `true`: The modal can be dismissed by clicking outside or pressing escape
   * - `"escapeOnly"`: The modal can be dismissed by pressing escape
   * - `false`: The modal cannot be dismissed
   */
  dismissable?: boolean | 'escapeOnly'
  /**
   * Callback when the modal exiting animation is finished
   */
  onExitComplete?: () => void
  children: ReactNode
  /**
   * Escape hatch for direct modal layer modification
   */
  modalLayerProps?: Omit<UIModalProps, 'children' | 'modalLayerProps'> &
    StyleProps
}

const layerStyle = tv({
  base: [
    'fixed inset-0 [--ui-modal-layer-bg:#0008] dark:[--ui-modal-layer-bg:#000b]',
    // Use the same z-index with MUI modals to prevent from overlapping issues
    'z-[1300]',
  ],
  variants: {
    type: {
      dialog: [
        'grid place-items-center px-4 overflow-auto',
        'pt-[max(16px,var(--safe-area-inset-top,env(safe-area-inset-top)))]',
        'pb-[max(16px,var(--safe-area-inset-bottom,env(safe-area-inset-bottom)))]',
      ],
      drawer: 'flex flex-col pt-12 pb-0 overflow-hidden',
    },
  },
  defaultVariants: {
    type: 'dialog',
  },
})

const isFirefox = () => /firefox/i.test(window.navigator.userAgent)

export function UIModalLayer(props: UIModalProps & StyleProps) {
  return (
    <AriaModalContext.Provider
      value={{
        isOpen: props.open,
        onOpenChange: isOpen => {
          if (!isOpen) {
            props.onClose?.()
          }
        },
        isDismissable: props.dismissable === true,
        isKeyboardDismissDisabled: props.dismissable === false,
      }}
    >
      <AnimatePresence onExitComplete={props.onExitComplete}>
        {props.open && (
          <MotionAriaModalOverlay
            isOpen
            data-ui="modal-layer"
            className={layerStyle({
              type: props.type,
              className: props.className,
            })}
            style={props.style}
            initial={{ backgroundColor: '#0000' }}
            animate={{ backgroundColor: 'var(--ui-modal-layer-bg)' }}
            exit={{ backgroundColor: '#0000' }}
            transition={{ duration: 0.2 }}
            shouldCloseOnInteractOutside={el => {
              // A workaround for Firefox where an extra click event
              // will be triggered on document.body when clicking in some menu
              if (isFirefox() && el === document.body) {
                return false
              }
              // Ignore interactions from browser extensions (e.g. 1Password,
              // Bitwarden). Extensions inject custom elements into the page,
              // such as `<com-1password-op-sidebar>` or `<bitwarden-callout>`.
              // Per the Web Components spec, custom element tags must contain
              // a hyphen. Extensions often use Shadow DOM; due to event
              // retargeting, `el` is the shadow host (the custom element).
              if (el.getRootNode() !== document) {
                return false
              }
              if (el.tagName.includes('-') && !el.hasAttribute('data-rac')) {
                return false
              }
              // Prevent dismissable modals from being closed by clicking
              // 3rd-party-created elements.
              // This may not be the best solution.
              // Feel free to change it if we have a better idea.
              const $blocker = el.closest(
                [
                  // The root container of toast elements.
                  '.Toastify',
                  // The root container of MUI popovers.
                  '.MuiPopover-root',
                  '.MuiPopper-root',
                  // The root container of PhotoSwipe elements.
                  '.pswp',
                  // Any other elements with dialog role that is not created by rac
                  '[role="dialog"]:not([data-rac])',
                ].join(','),
              )
              // If no such element is found, the modal can be closed
              return !$blocker
            }}
          >
            {props.children}
          </MotionAriaModalOverlay>
        )}
      </AnimatePresence>
    </AriaModalContext.Provider>
  )
}

const modalStyle = tv({
  variants: {
    type: {
      dialog: '',
      drawer: 'h-[calc(var(--visual-viewport-height)-48px)]',
    },
  },
  defaultVariants: {
    type: 'dialog',
  },
})

export function UIModal(props: UIModalProps & StyleProps) {
  // TODO: Remove dependency on `UIDialogContext`
  const { dragControls } = useContext(UIDialogContext)

  const { type, className, style, modalLayerProps, ...layerProps } = props

  const y = useMotionValue(0)

  function createMotionProps(type?: UIModalProps['type']) {
    switch (type) {
      case 'drawer':
        return {
          // `14px` is the height of the drag handle
          initial: { y: 'calc(100% + 14px)' },
          animate: { y: 0 },
          exit: { y: 'calc(100% + 14px)' },
          transition: { duration: 0.5, ease: [0.32, 0.72, 0, 1] },
          drag: 'y' as const,
          dragListener: false,
          dragControls,
          dragConstraints: { top: 0 },
          onDragEnd: (ev, { offset, velocity }) => {
            // Get the modal layer container height for more reliable calculations
            const modalElement = ev.target as HTMLElement
            const layerContainer = modalElement.closest(
              '[data-ui="modal-layer"]',
            ) as HTMLElement
            const containerHeight = layerContainer?.clientHeight || 800 // fallback height

            if (offset.y > containerHeight * 0.75 || velocity.y > 10) {
              props.onClose?.()
            } else {
              animate(y, 0, {
                type: 'inertia',
                bounceStiffness: 300,
                bounceDamping: 40,
                timeConstant: 300,
                min: 0,
                max: 0,
              })
            }
          },
        } satisfies MotionProps
      case 'dialog':
      default:
        return {
          initial: { opacity: 0, scale: 0.95 },
          animate: { opacity: 1, scale: 1 },
          transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
          exit: {
            opacity: 0,
            scale: 0.95,
            transition: { duration: 0.2, ease: [0.32, 0.72, 0, 1] },
          },
        } satisfies MotionProps
    }
  }

  return (
    <UIModalLayer {...layerProps} {...modalLayerProps} type={type}>
      <MotionAriaModal
        data-ui="modal"
        className={modalStyle({ type, className })}
        style={{ ...style, y }}
        {...createMotionProps(type)}
      >
        {type === 'drawer' && (
          // Drag handle
          <div
            className="flex justify-center items-end h-0"
            onPointerDown={ev => dragControls.start(ev)}
          >
            <div className="my-1 w-12 h-1.5 bg-white rounded-full shadow-sm shadow-black/50 ring-1 ring-black/10" />
          </div>
        )}
        {props.children}
      </MotionAriaModal>
    </UIModalLayer>
  )
}

const dialogStyles = tv({
  slots: {
    root: [
      'shadow-xl ring-1 outline-none',
      // Light
      'bg-white ring-black/10',
      // Dark
      'dark:bg-zinc-800 dark:shadow-black/20 dark:ring-white/10',
    ],
    header: 'p-4 msm:p-6',
    content: 'p-4 msm:p-6',
    footer: 'flex msm:justify-end flex-wrap gap-3 p-3',
  },
  variants: {
    type: {
      dialog: {
        root: 'min-w-[300px] rounded-xl',
        footer: 'rounded-b-xl',
      },
      drawer: {
        root: 'flex flex-col rounded-t-xl relative h-full overflow-auto',
        header: 'flex-none',
        content: 'flex-1 overflow-auto min-h-0',
        footer: 'flex-none',
      },
    },
    withHeader: {
      true: {
        content: 'pt-0 msm:pt-0',
        footer:
          'bg-zinc-100 dark:bg-[color-mix(in_srgb,theme(colors.zinc.800),#000_15%)] border-t border-zinc-200 dark:border-white/10',
      },
      false: {
        footer: 'gap-3 pt-0',
      },
    },
  },
  defaultVariants: {
    type: 'dialog',
    withHeader: false,
  },
})

const UIDialogContext = createContext<{
  type?: UIModalProps['type']
  setWithHeader: Dispatch<SetStateAction<boolean>>
  dragControls: DragControls
  styles: ReturnType<typeof dialogStyles>
  onClose?: UIModalProps['onClose']
}>({} as never)

export function UIDialog(
  props: UIModalProps &
    StyleProps & {
      modalClassName?: StyleProps['className']
      modalStyle?: StyleProps['style']
    },
) {
  const { className, style, modalClassName, modalStyle, ...modalProps } = props

  const [withHeader, setWithHeader] = useState(false)
  const dragControls = useDragControls()
  const styles = dialogStyles({ type: modalProps.type, withHeader })

  return (
    <UIDialogContext.Provider
      value={{
        type: modalProps.type,
        styles,
        setWithHeader,
        dragControls,
        onClose: props.onClose,
      }}
    >
      <UIModal {...modalProps} className={modalClassName} style={modalStyle}>
        <AriaDialog
          data-ui="dialog"
          className={styles.root({ className })}
          style={style}
        >
          {props.children}
        </AriaDialog>
      </UIModal>
    </UIDialogContext.Provider>
  )
}

type ManagedDialogCreateElements = {
  heading?: ComponentProps<typeof UIDialogHeader>['heading']
  content: ComponentProps<typeof UIDialogContent>['children']
  footer?: ComponentProps<typeof UIDialogFooter>['children']
  children?: never
}

/**
 * Should assemble Heading, Content and Footer manually
 * in the children
 *
 * T: Promise return value type
 *
 * If T is not void, should provide onResolve handler to `children`
 */
type UnmanagedDialogCreateElements<T = void> = {
  children: CloseableChildrenComponentType<T>
  heading?: never
  content?: never
  footer?: never
}

type UIDialogCreateProps<T = void> = Omit<
  ComponentProps<typeof UIDialog>,
  'open' | 'children'
> &
  (ManagedDialogCreateElements | UnmanagedDialogCreateElements<T>) & {
    responsive?: boolean
  }

UIDialog.create = async function <T = void>(props: UIDialogCreateProps<T>) {
  const { responsive, heading, content, footer, children, ...dialogProps } =
    props

  return new Promise<T>((resolve, reject) => {
    const destroy = dynamicCreateElement(
      <UseState initial={true}>
        {(open, setOpen) => {
          const dialogClose = () => {
            resolve(undefined as T)
            setOpen(false)
          }
          return (
            <UIDialogResponsive
              isMobile={responsive ? undefined : false}
              {...mergeProps(
                {
                  open,
                  onClose: dialogClose,
                  onExitComplete: () => {
                    destroy()
                  },
                },
                dialogProps,
              )}
              className={cn('max-w-[400px]', dialogProps.className)}
            >
              {/* Unmanaged */}
              {children ? (
                <CloseableChildren
                  onClose={() => {
                    setOpen(false)
                  }}
                  resolve={resolve}
                >
                  {children}
                </CloseableChildren>
              ) : (
                // Managed
                <>
                  {heading && <UIDialogHeader heading={heading} />}
                  <UIDialogContent>{content}</UIDialogContent>
                  {footer && <UIDialogFooter>{footer}</UIDialogFooter>}
                </>
              )}
            </UIDialogResponsive>
          )
        }}
      </UseState>,
    )
  })
}

type CloseableChildrenComponentType<T = void> =
  | ReactNode
  | ((state: {
      /** Close dialog with resolve(undefined) */
      close: () => void
      /** Close dialog without explicit resolve */
      closeDialog: () => void
      resolve: (val: T) => void
    }) => ReactNode)

function CloseableChildren<T>(props: {
  onClose: () => void
  resolve: (val: T) => void
  children?: CloseableChildrenComponentType<T>
}) {
  const { children, onClose, resolve } = props

  if (!children || !onClose) return null

  const resolveThenClose = () => {
    resolve(undefined as T)
    onClose()
  }
  return (
    <>
      {typeof children === 'function'
        ? children({
            close: resolveThenClose,
            closeDialog: onClose,
            resolve: props.resolve,
          })
        : children}
    </>
  )
}

UIDialog.alert = async (
  props:
    | string
    | ReactElement
    | (Omit<
        UIDialogCreateProps,
        'footer' | 'children' | 'contents' | 'header'
      > &
        ManagedDialogCreateElements & {
          confirmProps?: UIButtonProps
        }),
): Promise<void> => {
  const { confirmProps = undefined, ...dialogProps } =
    typeof props === 'object' && 'content' in props ? props : { content: props }

  return UIDialog.create({
    ...dialogProps,
    footer: ({ close }) => (
      <UIButton
        {...mergeProps(
          {
            variant: 'filled',
            className: 'max-msm:flex-1 min-w-16 capitalize',
            onPress: close,
          },
          confirmProps,
        )}
      >
        {confirmProps?.children ?? <Text i18nKey="action:ok" />}
      </UIButton>
    ),
  })
}

UIDialog.confirm = async (
  props:
    | string
    | ReactElement
    | (Omit<
        UIDialogCreateProps,
        'footer' | 'children' | 'contents' | 'header'
      > &
        ManagedDialogCreateElements & {
          confirmProps?: UIButtonProps
          cancelProps?: UIButtonProps
        }),
): Promise<boolean> => {
  const {
    confirmProps = undefined,
    cancelProps = undefined,
    ...dialogProps
  } = typeof props === 'object' && 'content' in props
    ? props
    : { content: props }
  let result = false

  return UIDialog.create({
    ...dialogProps,
    footer: ({ close }) => (
      <>
        <UIButton
          {...mergeProps(
            {
              className: 'max-msm:flex-1 min-w-16 capitalize',
              onPress: () => {
                result = false
                close()
              },
            },
            cancelProps,
          )}
        >
          {cancelProps?.children ?? <Text i18nKey="action:cancel" />}
        </UIButton>
        <UIButton
          {...mergeProps(
            {
              variant: 'filled',
              className: 'max-msm:flex-1 min-w-16 capitalize',
              onPress: () => {
                result = true
                close()
              },
            },
            confirmProps,
          )}
        >
          {confirmProps?.children ?? <Text i18nKey="action:confirm" />}
        </UIButton>
      </>
    ),
  }).then(() => result)
}

export function UIDialogHeader(
  props: StyleProps & {
    heading: string | ReactNode
    children?: (state: { heading: ReactNode }) => ReactNode
  },
) {
  const { setWithHeader, dragControls, styles } = useContext(UIDialogContext)

  useEffect(() => {
    setWithHeader(true)

    return () => setWithHeader(false)
  }, [setWithHeader])

  const heading = (
    <AriaHeading
      slot="title"
      className="text-lg -my-1"
      onPointerDown={ev => dragControls.start(ev)}
    >
      {props.heading}
    </AriaHeading>
  )

  return (
    <header
      data-ui="dialog-header"
      className={styles.header({ className: props.className })}
      style={props.style}
    >
      {props.children ? props.children({ heading }) : heading}
    </header>
  )
}

export function UIDialogContent(
  props: StyleProps & {
    children: ReactNode | ((state: { close: () => void }) => ReactNode)
  },
) {
  const { styles, onClose } = useContext(UIDialogContext)

  return (
    <div
      data-ui="dialog-content"
      className={styles.content({ className: props.className })}
      style={props.style}
    >
      {typeof props.children === 'function'
        ? props.children({ close: () => onClose?.() })
        : props.children}
    </div>
  )
}

export function UIDialogFooter(
  props: StyleProps & {
    children: ReactNode | ((state: { close: () => void }) => ReactNode)
  },
) {
  const { styles, onClose } = useContext(UIDialogContext)

  return (
    <footer
      data-ui="dialog-footer"
      className={styles.footer({ className: props.className })}
      style={props.style}
    >
      {typeof props.children === 'function'
        ? props.children({ close: () => onClose?.() })
        : props.children}
    </footer>
  )
}

export function UIDrawer(props: ComponentProps<typeof UIDialog>) {
  return <UIDialog {...props} type="drawer" />
}

export function UIDialogResponsive({
  isMobile,
  type,
  ...rest
}: ComponentProps<typeof UIDialog> & { isMobile?: boolean }) {
  const isMobileFromHook = useIsMobile()

  const dialogType = useMemo(() => {
    if (type) return type
    if (isAppWebview()) return 'drawer'
    if (isMobile ?? isMobileFromHook) return 'drawer'
    return 'dialog'
  }, [type, isMobile, isMobileFromHook])

  return <UIDialog type={dialogType} {...rest} />
}
